import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { api, download, type Paginated } from '../lib/api';
import { useI18n, type Locale } from '../lib/i18n';
import { usePeriod, type Period } from '../lib/period';
import type { EmployeeRow, ScoreLabel, ScoringDimension } from '../lib/types';
import { Badge, Button, Card, CardHeader, EmptyState, Field, PageHeader, Pagination, Select, Table, Td, Th, Tr, useToast } from '../components/ui';
import { KpiCard, PeriodLine, ScoreBadge, SnapshotStatus, useNumber } from '../components/kit';
import {
  FilterRow,
  Loaded,
  Note,
  ParamSelect,
  PersonLink,
  SCORE_LABEL_TONE,
  SCORE_LABELS,
  SearchBox,
  TextLink,
  TruncatedNote,
  useFilterOptions,
  usePercent,
} from '../components/b-shared';

/**
 * Employee Performance (CR-008 §6): each person's work score for the period — from the evidence of their
 * tasks, workflow steps, Q Tasks and tickets only — or "Not enough evidence", never a forced number. People
 * are grouped under their primary department and compared only within it: departments do different work.
 */

interface EmployeesPayload extends Paginated<EmployeeRow> {
  period: Period;
  settingsVersion: number;
  weights: Record<ScoringDimension, number>;
  summary: {
    people: number;
    scored: number;
    notEnoughEvidence: number;
    coveragePercent: number | null;
    medianScore: number | null;
    byLabel: Record<ScoreLabel, number>;
  };
  truncated: boolean;
}

const PAGE_SIZE = 200;
const DIMENSIONS: ScoringDimension[] = ['completion', 'deadline', 'quality', 'tickets', 'collaboration'];
const SORTS = ['name', 'score', 'evidence'] as const;
type Sort = (typeof SORTS)[number];

export function EmployeesPage() {
  const { t, locale, localized } = useI18n();
  const { params, param, setParam } = usePeriod();
  const toast = useToast();
  const filters = useFilterOptions();
  const departmentId = param('departmentId');
  const q = param('q');
  const sort: Sort = (SORTS as readonly string[]).includes(param('sort')) ? (param('sort') as Sort) : 'name';
  const page = Math.max(1, Number(param('page')) || 1);
  const [exporting, setExporting] = useState(false);

  const list = useQuery({
    queryKey: ['executive', 'employees', params, departmentId, q, page],
    queryFn: () => api.get<EmployeesPayload>('/executive/employees', { ...params, departmentId, q, page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  const exportCsv = async () => {
    setExporting(true);
    try {
      await download('/executive/employees/export.csv', { ...params, departmentId, q, locale });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <PageHeader
        title={t('employees.title')}
        subtitle={t('employees.subtitle')}
        count={list.data?.total}
        action={
          <Button icon={<Download className="h-4 w-4" aria-hidden />} loading={exporting} onClick={() => void exportCsv()}>
            {t('common.exportCsv')}
          </Button>
        }
      />
      <FilterRow>
        <ParamSelect
          param="departmentId"
          label={t('common.department')}
          allLabel={t('employees.allDepartments')}
          options={(filters.data?.departments ?? []).map((d) => ({ value: d.id, label: localized(d) }))}
        />
        <SearchBox label={t('common.search')} placeholder={t('employees.searchPlaceholder')} />
        <Field label={t('employees.sortLabel')} className="min-w-[11rem] sm:w-60">
          <Select value={sort} onChange={(e) => setParam('sort', e.target.value === 'name' ? null : e.target.value)}>
            {SORTS.map((s) => (
              <option key={s} value={s}>
                {t(`employees.sort.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
      </FilterRow>
      <Loaded query={list}>{(data) => <EmployeesBody data={data} sort={sort} departmentId={departmentId} />}</Loaded>
    </>
  );
}

function EmployeesBody({ data, sort, departmentId }: { data: EmployeesPayload; sort: Sort; departmentId: string }) {
  const { t, locale, localized } = useI18n();
  const { setParam, query } = usePeriod();
  const num = useNumber();
  const pct = usePercent();
  const groups = useMemo(
    () => groupRows(data.items, sort, locale, localized, t('employees.noDepartment'), departmentId),
    [data.items, sort, locale, localized, t, departmentId],
  );
  const s = data.summary;

  return (
    <div className="space-y-5">
      <PeriodLine period={data.period} />
      <TruncatedNote truncated={data.truncated} />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <KpiCard label={t('employees.kpi.people')} value={num(s.people)} hint={t('employees.kpi.peopleHint')} />
        <KpiCard label={t('employees.kpi.scored')} value={num(s.scored)} hint={t('employees.kpi.scoredHint')} />
        <KpiCard label={t('employees.kpi.notEnough')} value={num(s.notEnoughEvidence)} hint={t('employees.kpi.notEnoughHint')} />
        <KpiCard label={t('employees.kpi.coverage')} value={pct(s.coveragePercent)} hint={t('employees.kpi.coverageHint')} />
        <KpiCard
          label={t('employees.kpi.median')}
          value={s.medianScore === null ? t('work.na') : num(s.medianScore, 0)}
          hint={s.medianScore === null ? t('employees.kpi.medianNone') : t('employees.kpi.medianHint')}
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-5">
        <Card className="lg:col-span-2">
          <CardHeader title={t('employees.byLabel')} subtitle={t('employees.byLabelHint')} />
          <dl className="grid grid-cols-2 gap-2">
            {SCORE_LABELS.map((label) => (
              <div key={label} className="rounded-md bg-[var(--surface-sunken)] px-3 py-2.5">
                <dt>
                  <Badge tone={SCORE_LABEL_TONE[label]}>{t(`employees.label.${label}`)}</Badge>
                </dt>
                <dd className="mt-1.5 text-title tabular text-[var(--text-primary)]">{num(s.byLabel[label] ?? 0)}</dd>
              </div>
            ))}
          </dl>
        </Card>
        <Card className="lg:col-span-3">
          <CardHeader title={t('employees.method.title')} />
          <div className="space-y-2 text-body-sm text-[var(--text-secondary)]">
            <p>{t('employees.method.basis')}</p>
            <p>{t('employees.method.excluded')}</p>
            <p>{t('employees.method.nullScore')}</p>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {DIMENSIONS.map((d) => (
                <Badge key={d} tone="neutral" className="tabular">
                  {t(`dimension.${d}`)} · {num(data.weights[d] ?? 0)}%
                </Badge>
              ))}
            </div>
            <Note>
              {t('employees.method.weights', { version: num(data.settingsVersion) })}{' '}
              <TextLink to={`/settings?${query}`}>{t('nav.settings')}</TextLink>
            </Note>
          </div>
        </Card>
      </div>

      <Card padded={false}>
        <div className="px-5 pt-5">
          <CardHeader title={t('employees.tableTitle')} subtitle={t('employees.notCompared')} />
        </div>
        {data.items.length === 0 ? (
          <EmptyState title={t('employees.empty')} hint={t('employees.emptyHint')} />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('common.employee')}</Th>
                <Th>{t('employees.col.departments')}</Th>
                <Th>{t('employees.col.score')}</Th>
                <Th>{t('employees.col.confidence')}</Th>
                <Th align="end">{t('employees.col.evidence')}</Th>
                <Th align="end">{t('employees.col.measurable')}</Th>
                <Th align="end">{t('employees.col.tasks')}</Th>
                <Th align="end">{t('employees.col.steps')}</Th>
                <Th align="end">{t('employees.col.qTasks')}</Th>
                <Th align="end">{t('employees.col.tickets')}</Th>
                <Th align="end">{t('employees.col.missed')}</Th>
                <Th>{t('common.status')}</Th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <Fragment key={g.id || 'none'}>
                  <tr className="bg-[var(--surface-sunken)]">
                    <th scope="colgroup" colSpan={12} className="border-b hairline px-3 py-2 text-start text-caption font-semibold text-[var(--text-secondary)]">
                      {g.name} <span className="font-normal text-[var(--text-tertiary)]">· {t('employees.groupCount', { n: num(g.rows.length) })}</span>
                    </th>
                  </tr>
                  {g.rows.map((r) => (
                    <EmployeeTr key={r.user.id} row={r} query={query} />
                  ))}
                </Fragment>
              ))}
            </tbody>
          </Table>
        )}
        <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={(p) => setParam('page', String(p))} />
      </Card>
    </div>
  );
}

function EmployeeTr({ row, query }: { row: EmployeeRow; query: string }) {
  const { t, localized } = useI18n();
  const num = useNumber();
  const r = row.result;
  const m = r.metrics;
  const count = (v: number, warn = false) => (
    <Td align="end" className={warn && v > 0 ? 'tabular text-warning-fg dark:text-warning-dark' : 'tabular'}>
      {num(v)}
    </Td>
  );
  return (
    <Tr>
      <Td className="min-w-[12rem]">
        <span className="flex flex-wrap items-center gap-1.5">
          <PersonLink person={row.user} query={query} />
          {row.user.isActive === false && <Badge tone="neutral">{t('common.formerEmployee')}</Badge>}
        </span>
        {row.user.jobTitle && <span className="block text-caption text-muted">{row.user.jobTitle}</span>}
      </Td>
      <Td className="min-w-[9rem] text-[var(--text-secondary)]">{row.departments.length ? row.departments.map((d) => localized(d)).join(' · ') : '—'}</Td>
      <Td className="min-w-[10rem]">
        <ScoreBadge score={r.score} label={r.label} reason={r.reason} labelText={r.label ? t(`employees.label.${r.label}`) : undefined} />
        {r.score === null && r.reason && <span className="block text-caption text-[var(--text-tertiary)]">{t(`employees.reason.${r.reason}`)}</span>}
      </Td>
      <Td>{t(`employees.confidence.${r.confidence}`)}</Td>
      <Td align="end" className="tabular">
        {num(r.evidenceCount)}
      </Td>
      <Td align="end" className="tabular">
        {num(r.measurableWeight, 0)}%
      </Td>
      {count(m.tasksDelivered)}
      {count(m.workflowSteps)}
      {count(m.qTasksDone)}
      {count(m.ticketsResolved)}
      {count(m.dueMissed, true)}
      <Td>
        <span className="inline-flex flex-wrap items-center gap-1">
          <SnapshotStatus status={row.status} />
          {row.revision !== null && row.revision > 1 && <span className="text-caption text-muted">{t('employees.revision', { n: num(row.revision) })}</span>}
        </span>
      </Td>
    </Tr>
  );
}

/**
 * Each person under their primary department (or the first one they had) — or, with a department picked,
 * everyone under that one, since they all belong to it — departments by name, people by the chosen order.
 */
function groupRows(
  rows: EmployeeRow[],
  sort: Sort,
  locale: Locale,
  nameOf: (row: { name?: string | null; nameAr?: string | null; fullName?: string | null; fullNameAr?: string | null }) => string,
  noDepartment: string,
  departmentId: string,
) {
  const groups = new Map<string, { id: string; name: string; rows: EmployeeRow[] }>();
  for (const r of rows) {
    const picked = departmentId ? r.departments.find((d) => d.id === departmentId) : undefined;
    const primary = picked ?? r.departments.find((d) => d.isPrimary) ?? r.departments[0] ?? null;
    const id = primary?.id ?? '';
    let group = groups.get(id);
    if (!group) {
      group = { id, name: primary ? nameOf(primary) : noDepartment, rows: [] };
      groups.set(id, group);
    }
    group.rows.push(r);
  }
  const collator = new Intl.Collator(locale);
  const byName = (a: EmployeeRow, b: EmployeeRow) => collator.compare(nameOf(a.user), nameOf(b.user));
  const compare =
    sort === 'score'
      ? (a: EmployeeRow, b: EmployeeRow) => (b.result.score ?? -1) - (a.result.score ?? -1) || byName(a, b)
      : sort === 'evidence'
        ? (a: EmployeeRow, b: EmployeeRow) => b.result.evidenceCount - a.result.evidenceCount || byName(a, b)
        : byName;
  return [...groups.values()]
    .sort((a, b) => (a.id === '' ? 1 : b.id === '' ? -1 : collator.compare(a.name, b.name)))
    .map((g) => ({ ...g, rows: [...g.rows].sort(compare) }));
}
